import React from 'react';
import PropTypes from 'prop-types';

import { toast } from '@meronex/components';
import MdClose from '@meronex/icons/md/MdClose';

import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { useTheme } from '@material-ui/core/styles';

import { App } from '@meronex/app';

import IArchive from '@meronex/icons/fi/FiArchive';
import IRestore from '@meronex/icons/fi/FiRotateCcw';
import IRemove from '@meronex/icons/fi/FiTrash2';
import IBox from '@meronex/icons/fi/FiPackage';

import CircularProgress from '@material-ui/core/CircularProgress';

import { storesMng } from '../../api/storesMng';
import SitesUserName from '../../../common/SitesUserName';

function ArchivedStoreDialog(props) {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';

  const { open, onClose, onChange } = props;

  const [archivedStores, setArchivedStores] = React.useState();
  const [actionLoading, setActionLoading] = React.useState(null); // storeId being acted on
  const [confirmDelete, setConfirmDelete] = React.useState(null);

  React.useEffect(() => {
    if (open) {
      fetchArchivedStores();
    }
  }, [open]);

  const fetchArchivedStores = async () => {
    const result = await storesMng.getArchivedStores();
    setArchivedStores(result);
  };

  const onStoreRestore = async (store) => {
    setActionLoading(store._id);
    try {
      await storesMng.restoreStore(store._id);
      fetchArchivedStores();
      toast.success('Store restored successfully', { autoClose: 900 });
      onChange();
    } catch (e) {
      toast.error('Something went wrong');
      console.error(e);
    } finally {
      setActionLoading(null);
    }
  };

  const onStoreDelete = async (store) => {
    setConfirmDelete(null);
    setActionLoading(store._id);
    try {
      await storesMng.deleteStore(store._id);
      fetchArchivedStores();
      toast.success('Store deleted permanently', { autoClose: 900 });
      onChange();
    } catch (e) {
      toast.error('Something went wrong');
      console.error(e);
    } finally {
      setActionLoading(null);
    }
  };

  const _onClose = () => {
    setActionLoading(null);
    setConfirmDelete(null);
    onClose();
  };

  const mutedColor = isDark ? 'rgba(255,255,255,0.45)' : '#94a3b8';
  const cardBg = isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc';
  const cardBorder = isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0';
  const textColor = isDark ? 'rgba(255,255,255,0.85)' : '#1e293b';

  const actionBtnStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    border: 'none',
    transition: 'background-color 0.15s ease',
  };

  const renderEmpty = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '48px 20px',
      color: mutedColor,
    }}>
      <IArchive size={36} style={{ opacity: 0.3, marginBottom: '12px' }} />
      <div style={{ fontSize: '15px', fontWeight: 500 }}>No Archived Stores</div>
      <div style={{ fontSize: '13px', marginTop: '4px', opacity: 0.7 }}>
        Archived stores will appear here
      </div>
    </div>
  );

  const renderStoreCard = (store) => {
    const isLoading = actionLoading === store._id;
    const isConfirming = confirmDelete && confirmDelete._id === store._id;

    return (
      <div
        key={store._id}
        style={{
          backgroundColor: cardBg,
          border: `1px solid ${cardBorder}`,
          borderRadius: '12px',
          padding: '14px 16px',
          marginBottom: '10px',
          transition: 'border-color 0.15s ease',
        }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            backgroundColor: isDark ? 'rgba(99,102,241,0.12)' : '#eef2ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isDark ? 'rgba(99,102,241,0.8)' : '#6366f1',
            flexShrink: 0,
          }}>
            <IBox size={17} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: '14px',
              fontWeight: 600,
              color: textColor,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {store.name}
            </div>
            <div style={{
              fontSize: '12px',
              color: mutedColor,
              marginTop: '2px',
            }}>
              {store.itemCount || 0} item{store.itemCount !== 1 ? 's' : ''}
              {store.archived && (
                <>
                  {' · '}Archived {App.utils.time.fullFormat(store.archived.date)}
                  {store.archived.archivedBy && (
                    <> by <SitesUserName profile={store.archived.archivedBy} /></>
                  )}
                </>
              )}
            </div>
          </div>
          {isLoading && (
            <CircularProgress size={18} style={{ flexShrink: 0 }} />
          )}
        </div>

        {/* Action buttons */}
        {!isLoading && (
          <div style={{
            display: 'flex',
            gap: '8px',
            marginTop: '12px',
            paddingTop: '12px',
            borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}`,
          }}>
            <div
              style={{
                ...actionBtnStyle,
                backgroundColor: isDark ? 'rgba(16,185,129,0.1)' : '#ecfdf5',
                color: '#059669',
              }}
              onClick={() => onStoreRestore(store)}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(16,185,129,0.18)' : '#d1fae5'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(16,185,129,0.1)' : '#ecfdf5'; }}>
              <IRestore size={13} />
              <span>Restore</span>
            </div>
            {!isConfirming ? (
              <div
                style={{
                  ...actionBtnStyle,
                  backgroundColor: isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2',
                  color: isDark ? '#f87171' : '#dc2626',
                }}
                onClick={() => setConfirmDelete(store)}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(239,68,68,0.18)' : '#fee2e2'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2'; }}>
                <IRemove size={13} />
                <span>Delete</span>
              </div>
            ) : (
              <>
                <div
                  style={{
                    ...actionBtnStyle,
                    backgroundColor: isDark ? 'rgba(239,68,68,0.2)' : '#fee2e2',
                    color: isDark ? '#f87171' : '#dc2626',
                    fontWeight: 600,
                  }}
                  onClick={() => onStoreDelete(store)}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(239,68,68,0.3)' : '#fecaca'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(239,68,68,0.2)' : '#fee2e2'; }}>
                  <IRemove size={13} />
                  <span>Confirm Delete</span>
                </div>
                <div
                  style={{
                    ...actionBtnStyle,
                    backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                    color: mutedColor,
                  }}
                  onClick={() => setConfirmDelete(null)}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'; }}>
                  <span>Cancel</span>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  if (!archivedStores) {
    return null;
  }

  return (
    <Dialog
      className={'fullScreenMobile'}
      open={open}
      onClose={_onClose}
      maxWidth="sm"
      fullWidth>
      <DialogTitle>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <IArchive size={20} style={{ opacity: 0.6 }} />
          <span style={{ flex: 1 }}>Archived Stores</span>
          {archivedStores.length > 0 && (
            <span style={{
              fontSize: '12px',
              fontWeight: 500,
              color: mutedColor,
              backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
              padding: '2px 8px',
              borderRadius: '10px',
            }}>
              {archivedStores.length}
            </span>
          )}
          <div
            onClick={_onClose}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              cursor: 'pointer',
              color: isDark ? '#94a3b8' : '#64748b',
              flexShrink: 0,
              fontSize: '20px',
              transition: 'background-color 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
            <MdClose />
          </div>
        </div>
      </DialogTitle>
      <DialogContent style={{ paddingBottom: '20px' }}>
        {archivedStores.length === 0
          ? renderEmpty()
          : archivedStores.map((store) => renderStoreCard(store))
        }
      </DialogContent>
    </Dialog>
  );
}

ArchivedStoreDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onChange: PropTypes.func,
};

export default ArchivedStoreDialog;
